import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  InjectionToken,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CompanyProductPickerComponent } from './company-product-picker.component';
import type { PickerApplyResult, PickerData } from './company-product-picker.types';

/** Data injected into the fly-out by CarrierProductFlyOutService. */
export interface CarrierProductFlyOutData {
  /** Full picker tree (all carriers + products). Drives the picker's
   *  internal lookup; only the focused carrier's products are rendered. */
  data: PickerData;
  /** Carrier id to focus inside the picker. */
  focusedCarrierId: string;
  /** Pre-checked product ids. */
  initialSelectedProductIds: string[];
  /** External query — passed from the rail's "Filter the filters" input
   *  so typing in the rail also filters the fly-out's products. Signal so
   *  the host can update it live as the rail's query changes. */
  query: () => string;
  /** Live-emits on every selection change. Host writes to filter state. */
  selectionChange: EventEmitter<PickerApplyResult>;
}

export const CARRIER_PRODUCT_FLYOUT_DATA =
  new InjectionToken<CarrierProductFlyOutData>('CarrierProductFlyOutData');

/**
 * CarrierProductFlyOutComponent — the products-only overlay panel.
 *
 * Renders `<app-company-product-picker mode="products-only">` with the
 * carrier focused via [focusedCarrierIdInput] and the page-header search
 * query passed in via [externalSearchQuery]. Lives inside a CDK Overlay
 * panel positioned per-row by CarrierProductFlyOutService.
 */
@Component({
  selector: 'app-carrier-product-flyout',
  standalone: true,
  imports: [CommonModule, CompanyProductPickerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="cpf-panel">
      <app-company-product-picker
        mode="products-only"
        [dataInput]="ctx.data"
        [focusedCarrierIdInput]="focusedCarrierId()"
        [initialSelection]="initialSelection()"
        [externalSearchQuery]="query()"
        (selectionChange)="onSelection($event)" />
    </div>
  `,
  styles: [`
    :host { display: block; }
    .cpf-panel {
      width: 300px;
      background: #FFFFFF;
      border: 1px solid var(--border2, #DCE3EB);
      border-radius: 6px;
      box-shadow: var(--shadow-lg, 0 12px 32px rgba(13, 42, 69, 0.18));
      overflow: hidden;
      animation: cpf-in 0.18s ease-out;
    }
    @keyframes cpf-in {
      from { opacity: 0; transform: translateX(-6px); }
      to   { opacity: 1; transform: translateX(0); }
    }
  `],
})
export class CarrierProductFlyOutComponent {
  protected readonly focusedCarrierId = signal<string>('');
  protected readonly initialSelection = signal<{ selectedProductIds: string[] }>({
    selectedProductIds: [],
  });
  protected readonly query = signal<string>('');

  constructor(
    @Inject(CARRIER_PRODUCT_FLYOUT_DATA) protected readonly ctx: CarrierProductFlyOutData,
  ) {
    this.focusedCarrierId.set(ctx.focusedCarrierId);
    this.initialSelection.set({ selectedProductIds: [...ctx.initialSelectedProductIds] });
    // Sync external query — read on each microtask via re-render. The
    // service updates the data.query() return value (a closure over a
    // signal) and triggers detectChanges by re-attaching when needed; for
    // now we read once and rely on the service's reanchor() flow to
    // refresh on rail-search input changes.
    this.query.set(ctx.query());
  }

  /** Called by the service to re-anchor / re-focus this fly-out when the
   *  user clicks a different carrier. Updates the focused carrier id and
   *  initial-selection seed in place — the picker's @Input setters write
   *  internal signals so OnPush change-detection picks up the change. */
  setFocusedCarrier(carrierId: string, initialSelectedProductIds: string[]): void {
    this.focusedCarrierId.set(carrierId);
    this.initialSelection.set({ selectedProductIds: [...initialSelectedProductIds] });
  }

  /** Called by the service when the rail's search query changes. */
  setQuery(q: string): void {
    this.query.set(q);
  }

  protected onSelection(r: PickerApplyResult): void {
    this.ctx.selectionChange.emit(r);
  }
}
